import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { ChatBubbleLeftRightIcon, ChartBarIcon, UserGroupIcon } from '@heroicons/react/24/outline';

const AgentDemoPage = () => {
  const { agentName } = useParams();
  const location = useLocation();
  // /demos/:agentName is Serviti's own sales-demo surface (full marketing
  // pitch). The bare /:agentName catch-all is what customers' QR codes and
  // CTA links hit — their end users must NOT see Serviti's promo behind the
  // widget (reported 2026-07-16: Dhan-X QR scan showed Serviti's own
  // promotional banner to Dhan-X's prospects).
  const isSalesDemo = location.pathname.startsWith('/demos/');
  const [agent, setAgent] = useState(null);
  const [demoPage, setDemoPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [liveStats, setLiveStats] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [scrapedSources, setScrapedSources] = useState(null);

  const statusColors = {
    new: 'bg-blue-100 text-blue-800',
    contacted: 'bg-yellow-100 text-yellow-800',
    qualified: 'bg-green-100 text-green-800',
    converted: 'bg-purple-100 text-purple-800',
    closed: 'bg-gray-100 text-gray-600',
  };

  // Base URL without /api/v1 - we add it explicitly below
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const apiUrl = `${baseUrl}/api/v1`;

  useEffect(() => {
    const loadAgent = async () => {
      if (!agentName) {
        setError('No agent name provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // Convert agent name to slug (replace spaces with dashes)
        const slug = agentName.replace(/\s+/g, '-').replace(/[^\w-]/g, '');
        const response = await fetch(`${apiUrl}/agents/by-slug/${encodeURIComponent(slug)}`);
        if (!response.ok) {
          throw new Error('Agent not found');
        }
        const data = await response.json();
        setAgent(data);

        // Fetch demo_page content from template if not school
        if (data.prompt_template_name && data.prompt_template_name !== 'school_website_assistant.json') {
          try {
            const templateRes = await fetch(`${apiUrl}/agents/system-prompts/${encodeURIComponent(data.prompt_template_name)}`);
            if (templateRes.ok) {
              const templateData = await templateRes.json();
              if (templateData.demo_page) {
                setDemoPage(templateData.demo_page);
              }
            }
          } catch (templateErr) {
            console.warn('Could not fetch template demo_page:', templateErr);
          }
        }

        setError(null);

        // Fetch live analytics (public endpoint — no auth required)
        fetch(`${apiUrl}/analytics/public/${data.id}`)
          .then(r => r.json())
          .then(d => {
            if (d.success && d.stats?.total_conversations > 0) {
              setLiveStats(d.stats);
              if (d.recent_leads?.length > 0) setRecentLeads(d.recent_leads.slice(0, 2));
            }
          })
          .catch(() => {});

      } catch (err) {
        console.error('Error loading agent:', err);
        setError('Agent not found or unavailable');
      } finally {
        setLoading(false);
      }
    };

    loadAgent();
  }, [agentName, apiUrl]);

  // Fetch scraped pages for "trained on your website" section
  useEffect(() => {
    if (!agent?.rag_collection) return;
    const fetchSources = async () => {
      try {
        const colRes = await fetch(`${apiUrl}/rag/collections`);
        const colData = await colRes.json();
        const collection = colData.collections?.find(c => c.name === agent.rag_collection);
        if (!collection) return;
        const srcRes = await fetch(`${apiUrl}/rag/collections/${collection.id}/sources`);
        const srcData = await srcRes.json();
        setScrapedSources(srcData);
      } catch (e) { console.warn('Failed to load sources:', e); }
    };
    fetchSources();
  }, [agent, apiUrl]);

  // Customer entry (bare /:agentName — QR scans, CTA links): behave like a
  // wa.me link. Resolve the slug, then replace with the standalone full-page
  // chat (chat.html auto-opens, hides close, applies agent branding). The
  // existing query string is forwarded so utm_* campaign attribution on QR
  // links keeps flowing into lead capture.
  useEffect(() => {
    if (!agent || isSalesDemo) return;
    const params = new URLSearchParams(window.location.search);
    params.set('agent', agent.id);
    // chat.html puts this clean address back in the bar once it has loaded,
    // so a shared /your-agent link does not turn into chat.html?agent=<uuid>.
    const slug = window.location.pathname.replace(/^\/+|\/+$/g, '');
    if (/^[a-z0-9-]+$/i.test(slug)) params.set('slug', slug);
    window.location.replace(`/chat.html?${params.toString()}`);
  }, [agent, isSalesDemo]);

  // Inject the chat widget script when agent is loaded (sales-demo page only —
  // the customer entry above leaves for chat.html and must not flash a widget)
  useEffect(() => {
    if (!agent || !isSalesDemo) return;

    // Remove any existing widget
    const existingContainer = document.getElementById('serviti-chat-container');
    if (existingContainer) {
      existingContainer.remove();
    }
    const existingScript = document.getElementById('serviti-widget-script');
    if (existingScript) {
      existingScript.remove();
    }

    // Create and inject the widget script
    const script = document.createElement('script');
    script.id = 'serviti-widget-script';
    script.src = '/embed/serviti-chat-widget.js';
    // Only inject data-api-url in local dev — no deployed config exists then.
    // In built environments the widget self-resolves from serviti-widget-config.json
    // served beside it, so the demo exercises the decoupled path like a real embed.
    if (process.env.NODE_ENV === 'development') {
      script.setAttribute('data-api-url', apiUrl);
    }
    script.setAttribute('data-agent-id', agent.id);
    script.setAttribute('data-position', 'bottom-right');
    script.setAttribute('data-theme', 'light');
    script.setAttribute('data-auto-open', 'true');
    document.body.appendChild(script);

    // Cleanup on unmount
    return () => {
      const container = document.getElementById('serviti-chat-container');
      if (container) container.remove();
      const widgetScript = document.getElementById('serviti-widget-script');
      if (widgetScript) widgetScript.remove();
    };
  }, [agent, apiUrl, isSalesDemo]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading demo...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-500 text-6xl mb-4">🤖</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Demo Unavailable</h2>
          <p className="text-gray-600">{error}</p>
          <p className="text-sm text-gray-400 mt-4">
            The agent "{agentName}" could not be found. Please check the URL and try again.
          </p>
        </div>
      </div>
    );
  }

  // Determine which content to render
  const isSchool = agent.prompt_template_name === 'school_website_assistant.json';
  const hasDynamicContent = !isSchool && demoPage;

  const heroTitle = isSchool
    ? 'Convert parent inquiries into admissions with AI Chat assistant trained on your school data.'
    : hasDynamicContent
      ? demoPage.hero_title
      : agent.name;

  const heroSubtitle = isSchool
    ? 'Answers questions on programs, fees, campus visits, admissions deadlines, scholarships, and eligibility — instantly, 24/7.'
    : hasDynamicContent
      ? demoPage.hero_subtitle
      : agent.description || 'AI-powered assistant ready to help you 24/7. Click the chat bubble to start a conversation.';

  const heroTagline = !isSchool && hasDynamicContent ? demoPage.hero_tagline : null;
  const heroDescription = !isSchool && hasDynamicContent ? demoPage.hero_description : null;
  const heroChecklist = !isSchool && hasDynamicContent ? demoPage.hero_checklist : null;
  const heroAvailability = isSchool
    ? 'Available 24/7 — so you never miss a potential parent.'
    : 'Available 24/7 — so you never miss a potential customer.';

  const journeyHeading = isSchool ? 'How it works' : 'How the Agent journey works';
  const hasCustomJourney = demoPage?.how_it_works?.length > 0;
  const journeySteps = hasCustomJourney
    ? demoPage.how_it_works.map((step) => ({ title: step.title, description: step.description, pipeline: step.pipeline }))
    : isSchool
      ? [
          { title: 'Parents start chatting', description: 'Parents start chatting — on your school website, or by scanning the QR on social, flyer, or campus display.' },
          { title: 'AI answers instantly', description: 'Agent answers their questions instantly — programs, fees, admissions, curriculum, anything.' },
          { title: 'Leads captured automatically', description: 'Lead is captured automatically with parent contact and inquiry context.' },
          { title: 'Team notified in real-time', description: 'Your admissions team gets a real-time WhatsApp + email alert and moves the lead through contacted → visited → enrolled.' },
        ]
      : [
          { title: 'Customer starts chatting', description: 'Customer starts chatting — on your website, or by scanning the QR on social, flyer, or display.' },
          { title: 'AI answers instantly', description: 'Agent answers their questions instantly — products, services, pricing, anything.' },
          { title: 'Leads captured automatically', description: 'Lead is captured automatically with contact details and inquiry context.' },
          { title: 'Team notified in real-time', description: 'Your team gets a real-time WhatsApp + email alert and moves the lead through contacted → visited → converted.' },
        ];

  // Setup items — prefer demoPage, fall back to school or generic
  const setupItems = demoPage?.setup_items?.length > 0
    ? demoPage.setup_items
    : isSchool
      ? [
          'Automatically imports content from your existing school website',
          'Upload additional documents (prospectus, fee structure, brochures)',
          'No technical setup required',
          'Minimal involvement from your admissions team',
        ]
      : [
          'Automatically imports content from your existing website',
          'Upload additional documents',
          'No technical setup required',
          'Minimal involvement from your team',
        ];
  const setupTitle = demoPage?.setup_title || 'Fast & Hassle-Free Setup';
  const setupIntro = isSchool
    ? 'Your AI Admissions Office can be live on your school website in just a few hours.'
    : 'Serviti AI can be live on your website in just a few hours.';
  const setupFooter = 'From demo to live AI assistant — typically within the same day.';

  // Features (4 cards) — prefer demoPage, fall back to school defaults
  const features = demoPage?.features?.length > 0
    ? demoPage.features
    : isSchool
      ? [
          { title: 'Hidden Info → Instant Answers', description: 'Don\'t make parents hunt. Whether it\'s a PDF buried in a sub-menu or a line on the Fees page, the AI finds it in <3 seconds.', color: 'blue' },
          { title: 'Dead Ends → Hot Leads', description: 'A "Contact Us" form is a 24-hour wait. Our AI captures parent contact details the moment interest is highest — and instantly notifies your admissions team.', color: 'green' },
          { title: 'Scattered Leads → Admissions Pipeline', description: 'Every inquiry tracked from first message to enrollment. Filter, qualify, follow up — all in one place.', pipeline_stages: ['New', 'Contacted', 'Qualified', 'Enrolled'], color: 'purple' },
          { title: 'Manual Scoring → AI Priority', description: 'Hot leads (high intent, urgent inquiries) are flagged automatically — so your admissions team knows exactly who to call first.', color: 'orange' },
        ]
      : [];
  const showFeatures = features.length > 0;

  // Why Choose — prefer demoPage, fall back to school defaults
  const whyChooseItems = demoPage?.why_choose?.length > 0
    ? demoPage.why_choose
    : isSchool
      ? [
          '24/7 availability — never miss an admission inquiry',
          'Faster response time = more enrolments',
          'Saves admissions staff time on repetitive parent questions',
          'Clear visibility into your enrollment pipeline',
          'Built specifically for schools — admissions, programs, fees, eligibility, all handled',
        ]
      : [];
  const showWhyChoose = whyChooseItems.length > 0;

  const industryLabel = demoPage?.industry_label || (isSchool ? 'School Admissions' : 'Businesses');
  const whatYouGetTitle = `Your Complete ${industryLabel} Command Center`;
  const whatYouGetCopy = 'Everything your team needs — from first inquiry to conversion — in one intelligent platform.';
  const alertDescription = isSchool
    ? 'The moment a parent shows interest, your admissions team gets notified with full context.'
    : 'The moment a visitor shows interest, your team gets notified with full context — no lead slips through the cracks.';
  const whatsappNotificationImage = isSchool
    ? '/Serviti_WhatsApp_Notification.jpeg'
    : '/Serviti_WhatsApp_Notification_NonSchool.jpeg';
  const emailNotificationImage = isSchool
    ? '/Serviti_Email_Notification.jpeg'
    : '/Serviti_Email_Notification_NonSchool.jpeg';
  const dashboardTitle = demoPage?.dashboard_title || 'Your Leads Dashboard — Live Preview';
  const dashboardSubtitle = demoPage?.dashboard_subtitle || `See how ${isSchool ? 'your school\'s' : 'your'} AI assistant captures and qualifies ${isSchool ? 'parent' : 'customer'} inquiries automatically`;
  const defaultLeads = [
    { name: 'Priya Sharma', phone: '+91 98765 4XXXX', query: 'What is the curriculum for Grade 9? Do you offer CBSE and IB both?', status: 'qualified', priority: 'hot', date: '30 Mar', summary: 'Parent asked about Grade 9 curriculum options (CBSE vs IB). AI explained both streams, eligibility, and subject choices. Parent requested campus visit — lead captured.' },
    { name: 'Rahul Mehta', phone: '+91 91234 5XXXX', query: 'What are the admission cutoff dates for 2025-26? Is there still time to apply?', status: 'new', priority: 'warm', date: '31 Mar', summary: 'Parent inquired about 2025-26 admission deadlines. AI provided cutoff dates and next steps. Parent shared contact details for follow-up.' },
  ];
  const sampleLeads = demoPage?.sample_leads?.length ? demoPage.sample_leads : defaultLeads;

  const colorMap = {
    blue: { bg: 'from-blue-50 to-white', border: 'border-blue-100', iconBg: 'bg-blue-100', iconText: 'text-blue-600' },
    green: { bg: 'from-green-50 to-white', border: 'border-green-100', iconBg: 'bg-green-100', iconText: 'text-green-600' },
    purple: { bg: 'from-purple-50 to-white', border: 'border-purple-100', iconBg: 'bg-purple-100', iconText: 'text-purple-600' },
    orange: { bg: 'from-orange-50 to-white', border: 'border-orange-100', iconBg: 'bg-orange-100', iconText: 'text-orange-600' },
  };

  const featureIcons = [
    <ChatBubbleLeftRightIcon className="w-6 h-6" />,
    <span className="text-xl">🔔</span>,
    <ChartBarIcon className="w-6 h-6" />,
    <UserGroupIcon className="w-6 h-6" />,
  ];

  const pipelineColors = {
    blue: { bg: 'bg-blue-100', text: 'text-blue-700' },
    yellow: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
    purple: { bg: 'bg-purple-100', text: 'text-purple-700' },
    green: { bg: 'bg-green-100', text: 'text-green-700' },
  };
  const pipelineColorOrder = ['blue', 'yellow', 'purple', 'green'];

  // Customer entry: the redirect to /chat.html is in flight (effect above) —
  // show a bare splash for the moment the slug resolves. No page, no promo:
  // the destination is the full-page chat, like a wa.me link.
  if (!isSalesDemo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <p className="text-sm text-gray-500">Opening chat&hellip;</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img
                src="/serviti_logo_dark.png"
                alt="Serviti AI"
                className="h-12 object-contain"
                /* style={{ filter: 'invert(1) brightness(0.2)' }} */
              />
              <div>
                <h1 className="text-3xl font-extrabold text-blue-700">
                  {isSchool ? (agent.tenant_name || agent.name) : agent.name}
                </h1>
                <p className="text-sm text-gray-500">
                  Powered by <span className="font-semibold text-blue-600">Serviti AI</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Trial announcement */}
      <div className="bg-gradient-to-r from-blue-700 to-sky-600 text-white text-center py-4 px-4">
        <p className="text-lg sm:text-xl font-bold">
          🚀 🎉 <span className="text-yellow-300">START YOUR 30-DAY GROWTH TRIAL</span>{' '}
          <a
            href="https://app.serviti.ai/auth/login?mode=signup"
            target="_blank"
            rel="noopener noreferrer"
            className="underline font-extrabold hover:text-yellow-200 transition-colors text-xl"
          >
            Create your AI Agent →
          </a>
        </p>
      </div>

      {/* Main Content — right margin leaves room for the always-open chat widget */}
      <main className="max-w-5xl px-4 py-12 sm:px-6 lg:px-8 mx-auto lg:mr-[450px]">

        {/* Unified content for school and non-school pages */}
        <>
            {/* Hero Section */}
            <div className="text-center mb-12">
              <p className="text-sm font-semibold tracking-widest text-blue-600 uppercase mb-2">Serviti AI</p>
              <h2 className="text-3xl font-extrabold text-gray-900 mb-2">{heroTitle}</h2>
              {heroSubtitle && <p className="text-gray-600 max-w-2xl mx-auto mb-3">{heroSubtitle}</p>}
              {heroTagline && <p className="text-xl text-blue-700 font-bold mb-4">{heroTagline}</p>}
              {heroDescription && <p className="text-gray-600 max-w-2xl mx-auto mb-6">{heroDescription}</p>}
              {heroChecklist && (
                <ul className="inline-block text-left space-y-2 text-gray-700">
                  {heroChecklist.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✔</span> {item}
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-6 text-sm font-medium text-gray-500">{heroAvailability}</p>
              <div className="grid gap-6 items-start justify-center mx-auto max-w-4xl mb-8">
                <div className="rounded-3xl border border-blue-100 bg-blue-50 p-6 text-left shadow-sm">
                  <p className="text-sm font-semibold text-blue-700 mb-4">{journeyHeading}</p>
                  <ol className="space-y-4 text-sm text-gray-700">
                    {journeySteps.map((step, i) => (
                      <li key={i} className="flex gap-3">
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white">{i + 1}</span>
                        <div>
                          <p>{step.description}</p>
                          {step.pipeline && <p className="text-sm font-semibold text-green-700 mt-1">{step.pipeline}</p>}
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
              <div className="mt-6 grid gap-4 md:grid-cols-[minmax(0,220px)_minmax(0,1fr)] items-center mx-auto max-w-3xl rounded-2xl border border-gray-100 bg-white p-5 shadow-md">
                <div className="flex flex-col items-center rounded-xl border border-green-100 bg-green-50 p-4">
                  <p className="text-sm font-semibold text-gray-800 mb-3">Scan Sales WhatsApp QR</p>
                  <img
                    src="/qr-code8555811800.png"
                    alt="Scan to contact Serviti AI sales on WhatsApp"
                    className="w-36 h-36 object-contain"
                  />
                  <p className="text-xs text-gray-600 mt-3 max-w-[190px] text-center">
                    Opens WhatsApp sales for demo booking and agent setup.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row md:flex-col lg:flex-row gap-3 justify-center">
                  <a href="tel:+918555811800" className="inline-flex items-center justify-center w-full px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-md">
                    <span className="mr-2">📞</span> Call Us: +91 85558 11800
                  </a>
                  <a href="mailto:sales@serviti.ai?subject=Demo Request" className="inline-flex items-center justify-center w-full px-6 py-3 bg-gray-700 text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors shadow-md">
                    <span className="mr-2">📧</span> Email sales@serviti.ai
                  </a>
                </div>
              </div>
            </div>

            {/* Platform Features / What You Get — unified */}
            {showFeatures && (
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 mb-8">
                <p className="text-sm font-semibold tracking-widest text-purple-600 uppercase text-center mb-2">What You Get</p>
                <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">{whatYouGetTitle}</h3>
                <p className="text-gray-500 text-center mb-8 max-w-2xl mx-auto">{whatYouGetCopy}</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {features.map((feature, i) => {
                    const colors = colorMap[feature.color] || colorMap.blue;
                    return (
                      <div key={i} className={`rounded-xl border ${colors.border} bg-gradient-to-br ${colors.bg} p-6`}>
                        <div className="flex items-center gap-3 mb-3">
                          <div className={`w-10 h-10 ${colors.iconBg} rounded-lg flex items-center justify-center ${colors.iconText}`}>
                            {featureIcons[i] || featureIcons[0]}
                          </div>
                          <h4 className="font-bold text-gray-900 text-lg">{feature.title}</h4>
                        </div>
                        <p className="text-gray-600 text-sm mb-3">{feature.description}</p>
                        {feature.checklist && (
                          <ul className="text-sm text-gray-700 space-y-1 mb-4">
                            {feature.checklist.map((item, j) => (
                              <li key={j} className="flex items-start gap-2">
                                <span className="text-green-600 font-bold">✔</span> {item}
                              </li>
                            ))}
                          </ul>
                        )}
                        {feature.pipeline_stages && (
                          <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                            <div className="flex items-center justify-between text-xs font-semibold">
                              {feature.pipeline_stages.map((stage, j) => (
                                <React.Fragment key={j}>
                                  {j > 0 && <span className="text-gray-300">→</span>}
                                  <span className={`px-2 py-1 ${pipelineColors[pipelineColorOrder[j % pipelineColorOrder.length]].bg} ${pipelineColors[pipelineColorOrder[j % pipelineColorOrder.length]].text} rounded`}>
                                    {stage}
                                  </span>
                                </React.Fragment>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Why Choose — unified */}
            {showWhyChoose && (
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Why {industryLabel} Choose Serviti AI</h3>
                <ul className="space-y-3 text-gray-700">
                  {whyChooseItems.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-blue-600 font-bold">✓</span> {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* WhatsApp & Email Notifications Showcase */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 mb-8">
              <p className="text-sm font-semibold tracking-widest text-green-600 uppercase text-center mb-2">Real-Time Alerts</p>
              <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">Instant WhatsApp &amp; Email Notifications</h3>
              <p className="text-gray-500 text-center mb-8 max-w-2xl mx-auto">{alertDescription}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="rounded-xl border border-green-100 bg-gradient-to-br from-green-50 to-white p-6 text-center">
                  <div className="flex items-center gap-3 mb-3 justify-center">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <span className="text-xl">💬</span>
                    </div>
                    <h4 className="font-bold text-gray-900 text-lg">WhatsApp Alert</h4>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">Your team receives a WhatsApp message with lead details instantly.</p>
                  <img src={whatsappNotificationImage} alt="Serviti WhatsApp Notification" className="rounded-lg shadow-sm mx-auto max-h-[420px] w-full max-w-xs object-contain" />
                </div>
                <div className="rounded-xl border border-blue-100 bg-gradient-to-br from-blue-50 to-white p-6 text-center">
                  <div className="flex items-center gap-3 mb-3 justify-center">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <span className="text-xl">📧</span>
                    </div>
                    <h4 className="font-bold text-gray-900 text-lg">Email Alert</h4>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">Detailed email with conversation summary and contact information.</p>
                  <img src={emailNotificationImage} alt="Serviti Email Notification" className="rounded-lg shadow-sm mx-auto w-full max-w-lg object-contain" />
                </div>
              </div>
            </div>

            {/* Leads Management Dashboard */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 mb-8">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">{dashboardTitle}</h3>
                <p className="text-gray-500 text-sm mt-1">{dashboardSubtitle}</p>
              </div>

              {/* Static Stats Bar */}
              <div className="mx-auto max-w-3xl bg-gray-900 text-white rounded-xl px-6 py-5 mb-6">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-2xl font-extrabold text-green-400">500+</p>
                  <p className="text-xs text-gray-400 mt-0.5">Conversations/month</p>
                </div>
                <div>
                  <p className="text-2xl font-extrabold text-purple-400">98%</p>
                  <p className="text-xs text-gray-400 mt-0.5">Questions Answered</p>
                  <p className="text-xs text-gray-500 mt-0.5">Without human intervention</p>
                </div>
                <div>
                  <p className="text-2xl font-extrabold text-blue-400">40+</p>
                  <p className="text-xs text-gray-400 mt-0.5">Leads/month</p>
                  <p className="text-xs text-gray-500 mt-0.5">Captured automatically</p>
                </div>
                <div>
                  <p className="text-2xl font-extrabold text-amber-400">&lt;3s</p>
                  <p className="text-xs text-gray-400 mt-0.5">Response Time</p>
                  <p className="text-xs text-gray-500 mt-0.5">24/7 availability</p>
                </div>
              </div>
            </div>

              {/* Sample Recent Leads */}
              {(() => {
              const defaultLeads = [
                { name: 'Priya Sharma', phone: '+91 98765 4XXXX', query: 'Can you tell me about pricing and what\'s included in the package?', status: 'qualified', priority: 'hot', date: '30 Mar', summary: 'Visitor asked about pricing tiers. AI explained all packages, captured contact details for follow-up.' },
                { name: 'Rahul Mehta', phone: '+91 91234 5XXXX', query: 'Do you have availability next week? I\'d like to book a slot.', status: 'new', priority: 'warm', date: '31 Mar', summary: 'Visitor inquired about booking. AI confirmed availability and scheduled callback.' },
              ];
              const leads = (demoPage?.sample_leads?.length > 0) ? demoPage.sample_leads : defaultLeads;
              const priorityStyles = {
                hot: { bg: 'bg-red-100 text-red-700', dot: 'bg-red-500', label: 'Hot' },
                warm: { bg: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500', label: 'Warm' },
                cold: { bg: 'bg-sky-100 text-sky-700', dot: 'bg-sky-500', label: 'Cold' },
              };
              return (
                <div className="mx-auto max-w-3xl rounded-xl border border-gray-100 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                    <p className="text-sm font-semibold text-gray-700">Recent Leads — Live from this Agent</p>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">● Live</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Query</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {leads.map((lead, i) => {
                          const pStyle = priorityStyles[lead.priority] || priorityStyles.cold;
                          const statusBg = lead.status === 'qualified' ? 'bg-green-100 text-green-800' : lead.status === 'new' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700';
                          return (
                            <tr key={i} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <p className="font-medium text-gray-900">{lead.name}</p>
                                <p className="text-xs text-gray-500">{lead.phone}</p>
                              </td>
                              <td className="px-4 py-3 max-w-xs">
                                <p className="text-gray-700 truncate">{lead.query}</p>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBg}`}>{lead.status}</span>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full ${pStyle.bg}`}>
                                  <span className={`h-1.5 w-1.5 rounded-full ${pStyle.dot}`} />
                                  {pStyle.label}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{lead.date}</td>
                              <td className="px-4 py-3">
                                <button onClick={() => alert(lead.summary || 'AI captured visitor inquiry and qualified the lead.')} className="text-xs text-blue-600 hover:underline">Summary</button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
              })()}
            </div>

            {/* Fast & Hassle-Free Setup — unified */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">{setupTitle}</h3>
              <p className="text-gray-600 mb-4 text-center">{setupIntro}</p>
              <ul className="space-y-3 text-gray-700 max-w-3xl mx-auto">
                {setupItems.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-1 text-green-600 font-bold">✔</span>
                    {item}
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-sm text-gray-500 text-center">{setupFooter}</p>
            </div>
          </>

        {/* Tagline */}
        <div className="text-center p-6 bg-blue-50 rounded-xl border border-blue-100 mb-8">
          <p className="text-3xl text-blue-900 font-bold">{isSchool ? 'Every Unanswered Inquiry Is a Lost Enrollment.' : 'Every Unanswered Inquiry Is a Lost Customer.'}</p>
          <p className="text-blue-800 font-semibold mt-1">Don't let your static website cost you another student. We've built the engine — you just need the keys.</p>
        </div>

        {/* Contact & CTA */}
        <div className="mb-8">
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Ready to Get Started?</h3>
            <p className="text-gray-600 mb-6">{isSchool ? 'The admissions assistant is ready to go live on your school site — open the chat on the right, or request a quick demo.' : 'Talk to us — we\'ll set up a personalized demo for your business in minutes.'}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
              <a href="tel:+918555811800" className="inline-flex items-center justify-center w-full sm:w-52 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-md">
                <span className="mr-2">📞</span> Call +91 85558 11800
              </a>
              <a href="mailto:sales@serviti.ai?subject=Demo Request" className="inline-flex items-center justify-center w-full sm:w-52 px-6 py-3 bg-gray-700 text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors shadow-md">
                <span className="mr-2">📧</span> Email sales@serviti.ai
              </a>
            </div>
            <p className="text-sm text-gray-500">Or scan the QR code above to contact sales for a demo.</p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-400">
          <p>Powered by Serviti AI</p>
        </div>
      </main>
    </div>
  );
};

export default AgentDemoPage;
